import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";
import {
  CASE_STUDIES,
  CATEGORY_LABELS,
  CLIENT_REVIEWS,
  caseStudyListSchema,
  reviewSchema,
  type CaseStudyCategory,
} from "@/lib/case-studies";

const FAQS = [
  {
    q: "Can you tell me about matters like mine?",
    a: "Yes. We can talk you through how we have handled comparable Queensland conveyancing or estate matters, without identifying any client. Everything published here is de-identified and published with consent.",
  },
  {
    q: "Where can I read reviews of Coastside Law?",
    a: `Reviews left by clients on our Google Business Profile are public. If you have worked with us, a short review helps other Sunshine Coast families find a local firm. Call ${FIRM.phone} and we will send you the review link.`,
  },
  {
    q: "Do past results predict my result?",
    a: "No. Every property and every estate is different. Case studies describe what happened in a particular matter on its own facts; they are not a promise of the same outcome for you.",
  },
];

const ORDER: CaseStudyCategory[] = ["conveyancing", "wills-estates"];

export const Route = createFileRoute("/case-studies")({
  head: () => {
    const reviews = reviewSchema(CLIENT_REVIEWS);
    const cases = caseStudyListSchema(CASE_STUDIES);
    return {
      meta: [
        { title: fitTitle(`Case Studies & Client Reviews | ${FIRM.name}`) },
        {
          name: "description",
          content:
            fitDescription("De-identified examples of Sunshine Coast conveyancing, wills and estate matters handled by Coastside Law, plus reviews from clients in Golden Beach and Caloundra."),
        },
        { property: "og:title", content: fitTitle(`Case Studies & Client Reviews | ${FIRM.name}`) },
        {
          property: "og:description",
          content:
            fitDescription("Real conveyancing and estates matters, de-identified, plus what clients say about working with us."),
        },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: "/case-studies" },
        { name: "robots", content: "noindex, nofollow" },
      ],
      links: [{ rel: "canonical", href: "/case-studies" }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
        ...(cases ? [{ type: "application/ld+json", children: JSON.stringify(cases) }] : []),
        ...(reviews ? [{ type: "application/ld+json", children: JSON.stringify(reviews) }] : []),
      ],
    };
  },
  component: CaseStudiesPage,
});

function CaseStudiesPage() {
  return (
    <>
      <PageHero
        eyebrow="Our work"
        title="Case studies and client reviews"
        lead="Examples of the conveyancing and estate matters we handle on the Sunshine Coast, de-identified and published with consent — plus what clients say about working with us."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        {ORDER.map((category) => {
          const items = CASE_STUDIES.filter((c) => c.category === category);
          return (
            <div key={category} className="mb-14 last:mb-0">
              <h2 className="font-serif text-3xl text-foreground">{CATEGORY_LABELS[category]}</h2>
              {items.length === 0 ? (
                <p className="mt-4 rounded-sm border border-dashed border-border bg-secondary/40 p-6 text-sm leading-relaxed text-muted-foreground">
                  We are preparing de-identified case studies in this area. In the meantime, call{" "}
                  <a href={FIRM.phoneHref} className="font-semibold text-foreground underline">
                    {FIRM.phone}
                  </a>{" "}
                  and we will talk you through how we have handled matters like yours.
                </p>
              ) : (
                <div className="mt-6 space-y-6">
                  {items.map((item) => (
                    <article
                      key={item.id}
                      id={item.id}
                      className="rounded-sm border border-border bg-card p-7"
                    >
                      <h3 className="font-serif text-2xl text-foreground">{item.title}</h3>
                      {item.location && (
                        <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-accent-foreground/70">
                          {item.location}
                        </p>
                      )}
                      <dl className="mt-5 space-y-4 text-sm leading-relaxed">
                        <div>
                          <dt className="font-semibold text-foreground">The situation</dt>
                          <dd className="mt-1 text-muted-foreground">{item.situation}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold text-foreground">What we did</dt>
                          <dd className="mt-1 text-muted-foreground">{item.action}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold text-foreground">The outcome</dt>
                          <dd className="mt-1 text-muted-foreground">{item.outcome}</dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Case studies describe individual matters on their own facts. They are general
          information, not legal advice, and they are not a prediction of the result in your
          matter.
        </p>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <h2 className="font-serif text-3xl text-foreground">What clients say</h2>
          {CLIENT_REVIEWS.length === 0 ? (
            <p className="mt-4 rounded-sm border border-dashed border-border bg-card p-6 text-sm leading-relaxed text-muted-foreground">
              We are a new firm and we only publish reviews clients have actually written, so this
              space is deliberately empty for now. If we have acted for you, a short review on our
              Google Business Profile helps other Sunshine Coast families find us — call{" "}
              <a href={FIRM.phoneHref} className="font-semibold text-foreground underline">
                {FIRM.phone}
              </a>{" "}
              and we will send you the link.
            </p>
          ) : (
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              {CLIENT_REVIEWS.map((review) => (
                <figure key={review.id} className="rounded-sm border border-border bg-card p-7">
                  {review.rating && (
                    <p
                      className="text-sm tracking-widest text-accent-foreground"
                      aria-label={`${review.rating} out of 5 stars`}
                    >
                      {"★".repeat(review.rating)}
                      <span className="text-muted-foreground/50">
                        {"★".repeat(5 - review.rating)}
                      </span>
                    </p>
                  )}
                  <blockquote className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    “{review.body}”
                  </blockquote>
                  <figcaption className="mt-4 text-xs font-semibold uppercase tracking-widest text-foreground">
                    {review.author}
                    {review.location ? ` · ${review.location}` : ""}
                    {review.source ? (
                      <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground">
                        ({review.source})
                      </span>
                    ) : null}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-4">
        <EnquiryForm heading="Tell us about your matter" />
      </section>

      <CtaBand
        title="Want to talk it through first?"
        body="A short conversation usually tells you whether your matter is straightforward or needs a closer look. There is no charge for that first call."
      />
    </>
  );
}
